import React from 'react';
import { useTranslation } from 'react-i18next';
import { IssueCandidate } from 'api/api.types';
import RaceBar from 'lib/RaceBar/RaceBar';

interface Props {
  candidate: IssueCandidate;
  maxVotes: number;
  isFinal: boolean;
  candidateDetails?: {
    id: number;
    firstName: string;
    lastName: string;
    isUpToNow: boolean;
    partyAffiliation: string;
  }[];
}

const CandidateBar = ({ candidate, maxVotes, isFinal, candidateDetails }: Props) => {
  const { t } = useTranslation('wap');

  if (!candidate.results) return null;

  // On level of municipalities, candidate-list only contains id - name-key does not exist.
  // This is reassigned here if necessary

  // The check for id is required, as this list might also contain the entry for single_valid_votes
  // which uses same format as a candidate, has id = null and would not be found in candidateNames
  const firstName =
    candidateDetails && candidate.id
      ? candidateDetails.find(({ id }) => id === candidate.id)?.firstName
      : candidate.first_name;
  const lastName = candidateDetails
    ? candidateDetails.find(({ id }) => id === candidate.id)?.lastName
    : candidate.last_name;
  const isUpToNow =
    candidate.is_up_to_now ||
    candidateDetails?.find(({ id }) => id === candidate.id)?.isUpToNow;
  const partyAffiliation =
    candidate.party_affiliation ||
    candidateDetails?.find(({ id }) => id === candidate.id)?.partyAffiliation;

  const resultString = t('formatted_number', { count: candidate.results.votes });
  const screenReaderAppendix = t('votes_screen_reader_info');

  return (
    <div className="candidatebar">
      <div className="candidatebar__info">
        <span className="candidatebar__name">
          {`${firstName} `}
          {lastName && <span className="candidatebar__last-name">{lastName}</span>}
        </span>
        <span className="candidatebar__meta-items">
          {partyAffiliation && (
            <span className="candidatebar__meta-item">{`${partyAffiliation} `}</span>
          )}
          {isUpToNow && (
            <span className="candidatebar__meta-item">{`(${t('is_up_to_now')})`}</span>
          )}
        </span>
      </div>
      <RaceBar
        isFinal={isFinal}
        value={candidate.results.votes}
        maxValue={maxVotes}
        elected={candidate.results.elected}
        resultString={resultString}
        screenReaderAppendix={screenReaderAppendix}
      />
    </div>
  );
};

export default CandidateBar;
